import { Activity, Chore, Household, OfflineQueueItem, SyncActionTypes, Timestamp, UUID } from './chores';
import { User, UserRole } from './auth';

/**
 * Local mode (docs/plans/local-mode.md): the app used without an account, everything kept on the
 * phone alone. Its user is made up on the phone, and every household, chore and completion made
 * there carries that user's id until the data is carried into an account.
 */

/** Every local user's id starts with this, so it can never be taken for a Firebase uid. */
export const LOCAL_USER_PREFIX = 'local-';

/** The name a local user goes by in their households until they sign in. */
export const LOCAL_DISPLAY_NAME = 'Me';

export interface LocalIdentity {
  id: UUID;
  displayName: string;
  createdAt: Timestamp;
}

export const isLocalUserId = (id: UUID): boolean => id.startsWith(LOCAL_USER_PREFIX);

/** The local identity as the rest of the app knows a user: no email, so nothing that needs an
 * account (invitations, sync) finds anything to act on. */
export function localUser(identity: LocalIdentity): User {
  return {
    uid: identity.id,
    email: '',
    displayName: identity.displayName,
    roles: [UserRole.User],
    createdAt: new Date(identity.createdAt),
    lastSignIn: new Date(identity.createdAt),
  };
}

export interface LocalData {
  households: Household[];
  chores: Chore[];
  activities: Activity[];
}

/** How much was made in local mode, as a signed-in user is asked whether to add it to their account. */
export interface LocalDataCount {
  households: number;
  chores: number;
}

/** The account the local data is carried into. */
export interface AccountOwner {
  uid: UUID;
  displayName: string;
}

/**
 * The local data with the local user's id replaced by the account's wherever a user id is kept: a
 * household's owner, members (with the names that run parallel to them) and children, a chore's assignees and
 * last completer, a completion's completer. Anything not the local user's is left as it is.
 */
export function rekeyLocalData(data: LocalData, localId: UUID, account: AccountOwner): LocalData {
  const swap = (id: UUID): UUID => (id === localId ? account.uid : id);
  return {
    households: data.households.map((h) => ({
      ...h,
      owner_id: swap(h.owner_id),
      owner_display_name: h.owner_id === localId ? account.displayName : h.owner_display_name,
      member_ids: h.member_ids.map(swap),
      member_display_names: h.member_ids.map((id, i) =>
        id === localId ? account.displayName : h.member_display_names[i],
      ),
      ...(h.child_ids !== undefined && { child_ids: h.child_ids.map(swap) }),
    })),
    chores: data.chores.map((c) => ({
      ...c,
      assigned_to: c.assigned_to.map((a) => ({ ...a, userId: swap(a.userId) })),
      ...(c.last_completed_by !== undefined && { last_completed_by: swap(c.last_completed_by) }),
    })),
    activities: data.activities.map((a) => ({ ...a, by_user: swap(a.by_user) })),
  };
}

const byTime = <T>(time: (item: T) => Timestamp) => (a: T, b: T) => time(a).localeCompare(time(b));

/**
 * What the offline queue must push for the server to have the local data as it is now: each
 * household, then each chore, then each completion, oldest first within each. Built from what's
 * there rather than replaying what happened, so a chore made and deleted again in local mode, or a
 * completion undone, is never sent at all.
 *
 * The payloads are the ones choreSlice queues for the same actions, which syncService reads.
 */
export function buildUploadQueue(data: LocalData, now: Timestamp): OfflineQueueItem[] {
  const householdOf = new Map(data.chores.map((c) => [c.id, c.household_id]));
  return [
    ...[...data.households].sort(byTime((h) => h.created_at)).map((h): OfflineQueueItem => ({
      timestamp: now,
      type: SyncActionTypes.HOUSEHOLD_CREATED,
      householdId: h.id,
      data: {
        name: h.name,
        created_at: h.created_at,
        owner_id: h.owner_id,
        owner_display_name: h.owner_display_name,
        member_ids: h.member_ids,
        member_display_names: h.member_display_names,
      },
    })),
    ...[...data.chores].sort(byTime((c) => c.created_at)).map((c): OfflineQueueItem => ({
      timestamp: now,
      type: SyncActionTypes.CHORE_CREATED,
      householdId: c.household_id,
      choreId: c.id,
      data: {
        name: c.name,
        description: c.description ?? '',
        schedule: c.schedule,
        assigned_to: c.assigned_to,
        labels: c.labels ?? [],
        createdAt: c.created_at,
      },
    })),
    // A completion of a chore that's no longer there has nothing to be pushed onto.
    ...data.activities
      .filter((a) => householdOf.has(a.chore_id))
      .sort(byTime((a) => a.completed_at))
      .map((a): OfflineQueueItem => ({
        timestamp: now,
        type: SyncActionTypes.CHORE_COMPLETED,
        householdId: householdOf.get(a.chore_id),
        choreId: a.chore_id,
        data: { completedAt: a.completed_at, byUser: a.by_user },
      })),
  ];
}
